import {
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  Box,
  InputLabel,
  TextField,
  Typography
} from '@mui/material';
import { API } from 'aws-amplify';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import RefreshIcon from '@mui/icons-material/Refresh';
import IssuesOverviewTable from './components/IssuesOverviewTable';
import { DateTime } from 'luxon';

const IssueOverview = props => {
  const [issues, setIssues] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [assets, setAssets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [orderBy, setOrderBy] = useState('lastCheck');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [assetType, setAssetType] = useState("ALL");
  const [issueType, setIssueType] = useState("ALL");
  const [location, setLocation] = useState("ALL");
  const [severity, setSeverity] = useState("ALL");
  const [issueState, setIssueState] = useState("OPEN");
  const today = DateTime.now();
  const tomorrow = today.plus({ days: 1 });
  const [startDate, setStartDate] = useState(today.toFormat('yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(tomorrow.toFormat('yyyy-MM-dd'));
  const ALL_ASSET = { id: 'ALL', assetName: 'ALL' };
  const [asset, setAsset] = useState<Asset>(ALL_ASSET);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  useEffect(() => {
    filterIssues();
    setIsLoading(false);
  }, [issues, assetType, issueType, location, severity, asset, searchTerm, startDate, endDate]);

  useEffect(() => {
    loadAndSetIssues();
  }, [issueState]);

  interface Asset {
    id?: string;
    assetName?: string;
    assetType?: string;
  }

  useEffect(() => {
    loadAndSetIssues();
    loadAndSetAssets();
  }, [API, assetType]);

  function loadAndSetIssues() {
    setIsLoading(true);
    API.get('bom', `/issue?state=${issueState}`, {})
      .then(a => {
        console.log("load issues");
        setIssues(a);
      })
      .catch(e => {
        console.error(e);
        setIsLoading(false);
      });
  }

  function closeIssue(closingNote, issueId) {
    setIsLoading(true);
    API.post('bom', `/issue/close/${issueId}?closingNote=${closingNote}`, {
      body: {}
    })
      .then(a => {
        loadAndSetIssues();
        setIsLoading(false);
      })
      .catch(e => {
        console.error(e);
        setIsLoading(false);
      });
  }

  function loadAndSetAssets() {
    setIsLoading(true);
    API.get('bom', `/asset?assetType=${assetType}&materialViewQuery=false`, {})
      .then(a => {
        setAssets(a);
        setIsLoading(false);
      })
      .catch(e => {
        console.error(e);
        setIsLoading(false);
      });
  }

  const filterIssues = () => {
    let newFilteredIssues = issues;

    if (issueType !== "ALL") {
      newFilteredIssues = newFilteredIssues.filter(issue => issue.issueCategory === issueType);
    }

    if (assetType !== "ALL") {
      newFilteredIssues = newFilteredIssues.filter(issue => issue.assetType === assetType);
    }

    if (location !== "ALL") {
      newFilteredIssues = newFilteredIssues.filter(issue => issue.location === location);
    }

    if (severity !== "ALL") {
      newFilteredIssues = newFilteredIssues.filter(issue => issue.severity === severity);
    }

    if (asset.assetName !== "ALL") {
      newFilteredIssues = newFilteredIssues.filter(issue => issue.assetName === asset.assetName);
    }

    if (searchTerm !== '') {
      newFilteredIssues = newFilteredIssues.filter(item =>
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.detail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.note?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.issueCategory?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.issueSubCategory?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (startDate) {
      const startTimestamp = new Date(startDate).getTime();
      newFilteredIssues = newFilteredIssues.filter(issue => {
        const result = issue.created >= startTimestamp;
        return result;
      });
    }

    if (endDate) {
      const endTimestamp = new Date(endDate).getTime();
      newFilteredIssues = newFilteredIssues.filter(issue => {
        const result = issue.created <= endTimestamp;
        return result;
      });
    }

    newFilteredIssues.sort((a, b) => b.created - a.created);

    setFilteredIssues(newFilteredIssues);
  };

  const handleAssetTypeFilterChange = event => setAssetType(event.target.value);
  const handleIssueTypeFilterChange = event => setIssueType(event.target.value);
  const handleLocationFilterChange = event => setLocation(event.target.value);
  const handleSeverityFilterChange = event => setSeverity(event.target.value);

  function handleIssueStateFilterChange(event) {
    setIssueState(event.target.value);
  }

  function handleAssetFilterChange(event) {
    if (event.target.value === 'ALL') {
      setAsset(ALL_ASSET);
    } else {
      setAsset(event.target.value);
    }
  }

  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  function getComparator<Key extends keyof any>(
    order: 'desc' | 'asc',
    orderBy: Key
  ): (
      a: { [key in Key]: number | string },
      b: { [key in Key]: number | string }
    ) => number {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property,
    order
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div
      style={{
        padding: 12
      }}>
      <Box display="flex" alignItems="center" gap={2}>
      <Typography>
        Issues: {filteredIssues.length}
      </Typography>
      <FormControl style={{
          display: 'flex',
          flexDirection: 'row',
        }}>
          <InputLabel>Issue State Filter</InputLabel>
          <Select
            value={issueState}
            label="ISSUE STATE"
            style={{ minWidth: 120 }}
            onChange={handleIssueStateFilterChange}>
            <MenuItem value={'OPEN'}>open</MenuItem>
            <MenuItem value={'COMPLETE'}>complete</MenuItem>
            <MenuItem value={'INFO'}>info</MenuItem>
          </Select>
        </FormControl>
        <FormControl style={{
          display: 'flex',
          flexDirection: 'row',
        }}>
          <InputLabel>Issue Type Filter</InputLabel>
          <Select
            value={issueType}
            label="ISSUE TYPES"
            style={{ minWidth: 120 }}
            onChange={handleIssueTypeFilterChange}>
            <MenuItem value="ALL">all</MenuItem>
            <MenuItem value={'ASSET_SOFTWARE'}>asset software</MenuItem>
            <MenuItem value={'ASSET_HARDWARE'}>asset hardware</MenuItem>
            <MenuItem value={'OPS'}>ops</MenuItem>
            <MenuItem value={'COMMERCIAL'}>commercial</MenuItem>
          </Select>
        </FormControl>

        <FormControl style={{
          display: 'flex',
          flexDirection: 'row',
        }}>
          <InputLabel>Location Filter</InputLabel>
          <Select
            value={location}
            label="Location"
            style={{ minWidth: 120 }}
            onChange={handleLocationFilterChange}>
            <MenuItem value="ALL">all</MenuItem>
            <MenuItem value={'BL'}>blanch</MenuItem>
            <MenuItem value={'PS'}>pecan square</MenuItem>
            <MenuItem value={'DR'}>dr testing</MenuItem>
            <MenuItem value={'MR'}>383 testing</MenuItem>
          </Select>
        </FormControl>

        <FormControl style={{
          display: 'flex',
          flexDirection: 'row',
        }}>
          <InputLabel>Severity Filter</InputLabel>
          <Select
            value={severity}
            label="Severity"
            style={{ minWidth: 120 }}
            onChange={handleSeverityFilterChange}>
            <MenuItem value="ALL">all</MenuItem>
            <MenuItem value={'NO_IMPACT'}>no impact</MenuItem>
            <MenuItem value={'INCREASED_WORKLOAD'}>increased workload</MenuItem>
            <MenuItem value={'ORDER_DELAYS'}>order delays</MenuItem>
            <MenuItem value={'MINOR_MAINTENANCE'}>minor maintenance</MenuItem>
            <MenuItem value={'AIRCRAFT_ON_GROUND'}>aircraft on ground</MenuItem>
            <MenuItem value={'OPERATIONS_STOPPED'}>operations stopped</MenuItem>
          </Select>
        </FormControl>

        <FormControl style={{
          display: 'flex',
          flexDirection: 'row',
        }}>
          <InputLabel>Asset Type Filter</InputLabel>
          <Select
            value={assetType}
            label="ASSET TYPES"
            style={{ minWidth: 120 }}
            onChange={handleAssetTypeFilterChange}>
            <MenuItem value="ALL">all</MenuItem>
            <MenuItem value={'AIRCRAFT'}>aircraft</MenuItem>
            <MenuItem value={'CASSETTE'}>cassette</MenuItem>
            <MenuItem value={'SPOOL'}>spool</MenuItem>
            <MenuItem value={'CHARGER'}>charger</MenuItem>
            <MenuItem value={'BATTERY'}>battery</MenuItem>
            <MenuItem value={'RC'}>rc</MenuItem>
            <MenuItem value={'TABLET'}>tablet</MenuItem>
          </Select>
        </FormControl>

        <FormControl style={{ display: 'flex', flexDirection: 'row' }}>
          <InputLabel>Asset Filter</InputLabel>
          <Select
            value={asset}
            label="ASSETS"
            style={{ minWidth: 120 }}
            onChange={handleAssetFilterChange}
            renderValue={selected => (selected as Asset).assetName.toLowerCase()}

          >
            <MenuItem key={0} value="ALL">all</MenuItem>
            {assets.map((asset) => (
              <MenuItem key={asset.id} value={asset}>{asset.assetName}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <div className="mt-2">
          <input
            type="date"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
            id="start-date"
            name="start-date"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setStartDate(e.target.value);
            }}
            value={startDate}
          />
        </div>
        <div className="mt-2">
          <input
            type="date"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
            id="end-date"
            name="end-date"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setEndDate(e.target.value);
            }}
            value={endDate}
          />
        </div>

        <Box sx={{ display: 'flex', alignItems: 'center', width: 'auto' }}>
          <TextField
            label="Search"
            variant="outlined"
            sx={{ mr: 1 }}
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </Box>

        <Tooltip title="Refresh">
          <IconButton color="primary" onClick={loadAndSetIssues}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>

      </Box>

      <IssuesOverviewTable
        onRequestSort={handleRequestSort}
        issues={filteredIssues.slice().sort(getComparator(order, orderBy))}
        isLoading={isLoading}
        closeIssue={closeIssue}
      />

    </div>
  );
};

const mapStateToProps = state => ({});
const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(IssueOverview);
